import React, { createContext, useCallback, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router';
import { useFetchClient } from '../utils/fetch';
import { useConversationSocket } from '../utils/socketClient';
import {
  clearLoginRetryAt,
  getLoginRetryAt,
  getRetryAfterSeconds,
  setLoginRetryAt,
} from '../utils/loginCooldown';

export const ChatContext = createContext(null);
const initialState = {
  text: '',
  files: [],
  isSending: false,
  conversationId: null,
  conversationData: null,
  quickReplies: [],
  view: 'chat', // 'chat' or 'quick-replies'
  chatType: null,

  unread: {
    total: 0,
    customer: 0,
    guest: 0,
  },

  lastReadConversationId: null,
  lastReadAt: null,
};
const chatReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TEXT':
      return { ...state, text: action.payload };
    case 'SET_FILES':
      return {
        ...state,
        files: [
          ...state.files,
          ...(Array.isArray(action.payload) ? action.payload : [action.payload]),
        ],
      };
    case 'REMOVE_FILE':
      return {
        ...state,
        files: state.files.filter((_, index) => index !== action.payload),
      };
    case 'CLEAR_FILES':
      return { ...state, files: [] };
    case 'SET_IS_SENDING':
      return {
        ...state,
        isSending: action.payload,
      };
    case 'SET_UNREAD_COUNTS': {
      const customer = Number(action.payload?.customer || 0);

      const guest = Number(action.payload?.guest || 0);

      return {
        ...state,
        unread: {
          total: customer + guest,
          customer,
          guest,
        },
      };
    }
    case 'SET_CHAT_TYPE': {
      const chatType = action.payload;

      if (state.chatType !== chatType) {
        return {
          ...state,
          chatType,
          conversationId: null,
          conversationData: null,
          view: 'chat',
        };
      }

      return {
        ...state,
        chatType,
      };
    }
    case 'RESET_CHAT':
      return {
        ...initialState,
        unread: state.unread,
        quickReplies: state.quickReplies,
        conversationId: state.conversationId,
        conversationData: state.conversationData,
        chatType: state.chatType,
        view: state.view,
      };
    case 'SET_CONVERSATION':
      return {
        ...state,
        conversationId: action.payload.id,
        conversationData: action.payload.item,
        chatType: action.payload.type,
        view: 'chat',
      };
    case 'UPDATE_CONVDATA':
      return {
        ...state,
        conversationData: action.payload,
      };
    case 'UPDATE_CONVERSATION_STATUS':
      if (state.conversationId !== action.payload.id || !state.conversationData) return state;

      return {
        ...state,
        conversationData: {
          ...state.conversationData,
          status: action.payload.status,
        },
      };
    case 'SET_QUICKREPLIES':
      return {
        ...state,
        quickReplies: Array.isArray(action.payload) ? action.payload : [],
      };
    case 'RESET_QUICKREPLIES':
      return {
        ...state,
        quickReplies: Array.isArray(action.payload) ? action.payload : [],
      };
    case 'SET_VIEW':
      return {
        ...state,
        view: action.payload,
      };
    case 'READ_SUCCESS':
      return {
        ...state,
        lastReadConversationId: action.payload.id,
        lastReadAt: Date.now(),
      };

    default:
      return state;
  }
};

export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const location = useLocation();
  const isChatRoute = location.pathname === '/chat' || location.pathname === '/chat-guest';
  const { fetchClient } = useFetchClient();

  const [customerToken, setCustomerToken] = useState(() =>
    localStorage.getItem('Pulsalink-message-token')
  );

  const [guestToken, setGuestToken] = useState(() =>
    localStorage.getItem('Pulsalink-message-token-guest')
  );
  const [loginIssue, setLoginIssue] = useState(null);

  const customerLoginRef = React.useRef(false);
  const guestLoginRef = React.useRef(false);
  const unreadRequestRef = React.useRef(null);
  const unreadRefreshTimeoutRef = React.useRef(null);
  const activeChatScope =
    location.pathname === '/chat'
      ? 'customer'
      : location.pathname === '/chat-guest'
        ? 'guest'
        : null;

  const loginCustomerChat = useCallback(async () => {
    if (customerLoginRef.current) {
      return null;
    }

    const retryAt = getLoginRetryAt();
    if (retryAt) {
      setLoginIssue({ scope: 'customer', status: 'rate-limited', retryAt });
      return null;
    }

    customerLoginRef.current = true;
    setLoginIssue({ scope: 'customer', status: 'loading' });

    try {
      const login = await fetchClient(
        'customers/login',
        'POST',
        {
          email: 'cs@pulsalink.id',
          password: '58652048:1',
        },
        {
          auth: false,
          silent: true,
        }
      );

      if (!login?.success || !login?.token) {
        const error = new Error(login?.error || login?.message || 'Customer chat login failed');
        error.status = login?.retryAfter ? 429 : login?.status;
        error.retryAfter = login?.retryAfter;
        error.data = login;
        throw error;
      }

      localStorage.setItem('Pulsalink-message-token', login.token);

      localStorage.setItem('Pulsalink-message-user', JSON.stringify(login.data));

      setCustomerToken(login.token);
      clearLoginRetryAt();
      setLoginIssue((current) => (current?.scope === 'customer' ? null : current));

      return login;
    } catch (error) {
      setCustomerToken(null);
      if (
        error?.status === 429 ||
        error?.data?.retryAfter ||
        /too many attempts/i.test(error?.message || '')
      ) {
        const nextRetryAt = setLoginRetryAt(getRetryAfterSeconds(error));
        setLoginIssue({ scope: 'customer', status: 'rate-limited', retryAt: nextRetryAt });
      } else {
        setLoginIssue({ scope: 'customer', status: 'error' });
      }

      return null;
    } finally {
      customerLoginRef.current = false;
    }
  }, [fetchClient]);

  const loginGuestChat = useCallback(async () => {
    if (guestLoginRef.current) {
      return null;
    }

    const retryAt = getLoginRetryAt();
    if (retryAt) {
      setLoginIssue({ scope: 'guest', status: 'rate-limited', retryAt });
      return null;
    }

    guestLoginRef.current = true;
    setLoginIssue({ scope: 'guest', status: 'loading' });

    try {
      const login = await fetchClient(
        'customers/login',
        'POST',
        {
          email: 'csguest@pulsalink.id',
          password: '58652048:1',
        },
        {
          auth: false,
          silent: true,
        }
      );

      if (!login?.success || !login?.token) {
        const error = new Error(login?.error || login?.message || 'Guest chat login failed');
        error.status = login?.retryAfter ? 429 : login?.status;
        error.retryAfter = login?.retryAfter;
        error.data = login;
        throw error;
      }

      localStorage.setItem('Pulsalink-message-token-guest', login.token);

      localStorage.setItem('Pulsalink-message-user-guest', JSON.stringify(login.data));

      setGuestToken(login.token);
      clearLoginRetryAt();
      setLoginIssue((current) => (current?.scope === 'guest' ? null : current));

      return login;
    } catch (error) {
      setGuestToken(null);
      if (
        error?.status === 429 ||
        error?.data?.retryAfter ||
        /too many attempts/i.test(error?.message || '')
      ) {
        const nextRetryAt = setLoginRetryAt(getRetryAfterSeconds(error));
        setLoginIssue({ scope: 'guest', status: 'rate-limited', retryAt: nextRetryAt });
      } else {
        setLoginIssue({ scope: 'guest', status: 'error' });
      }

      return null;
    } finally {
      guestLoginRef.current = false;
    }
  }, [fetchClient]);

  useEffect(() => {
    if (activeChatScope !== 'customer') return;

    const token = localStorage.getItem('Pulsalink-message-token');

    const user = localStorage.getItem('Pulsalink-message-user');

    if (token && user) {
      console.log('CUSTOMER CHAT SESSION EXISTS');

      setCustomerToken(token);
      return;
    }

    localStorage.removeItem('Pulsalink-message-token');
    localStorage.removeItem('Pulsalink-message-user');
    setCustomerToken(null);
    loginCustomerChat();
  }, [activeChatScope, loginCustomerChat]);

  useEffect(() => {
    if (activeChatScope !== 'guest') return;

    const token = localStorage.getItem('Pulsalink-message-token-guest');

    const user = localStorage.getItem('Pulsalink-message-user-guest');

    if (token && user) {
      console.log('GUEST CHAT SESSION EXISTS');

      setGuestToken(token);
      return;
    }

    localStorage.removeItem('Pulsalink-message-token-guest');
    localStorage.removeItem('Pulsalink-message-user-guest');
    setGuestToken(null);
    loginGuestChat();
  }, [activeChatScope, loginGuestChat]);

  const getUnreadCounts = useCallback(async () => {
    if (unreadRequestRef.current) {
      return unreadRequestRef.current;
    }

    const request = (async () => {
      try {
        const currentCustomerToken = localStorage.getItem('Pulsalink-message-token');

        const currentGuestToken = localStorage.getItem('Pulsalink-message-token-guest');

        const messageUser = JSON.parse(localStorage.getItem('Pulsalink-message-user') || 'null');

        const guestUser = JSON.parse(
          localStorage.getItem('Pulsalink-message-user-guest') || 'null'
        );

        let unreadCustomer = 0;
        let unreadGuest = 0;

        if (currentCustomerToken && messageUser) {
          const currentUserId = messageUser.id ?? messageUser._id;

          const responseCustomer = await fetchClient(
            'conversation',
            'GET',
            {
              page: 1,
            },
            {
              token: currentCustomerToken,
              silent: true,
            }
          );

          if (responseCustomer?.success) {
            const conversations = Array.isArray(responseCustomer.data)
              ? responseCustomer.data
              : responseCustomer.data?.list || [];

            unreadCustomer = conversations.reduce((total, item) => {
              const participant = item.participants?.find((participant) => {
                const participantUserId =
                  participant.userID?._id ??
                  participant.userID?.id ??
                  participant.userID ??
                  participant._id;

                return String(participantUserId) === String(currentUserId);
              });

              return total + Number(participant?.unReadCount || 0);
            }, 0);
          }
        }

        if (currentGuestToken && guestUser) {
          const guestId = guestUser._id ?? guestUser.id;

          const responseGuest = await fetchClient(
            'guests/conversations',
            'GET',
            {
              page: 1,
            },
            {
              token: currentGuestToken,
              silent: true,
            }
          );

          if (responseGuest?.success) {
            const conversations = Array.isArray(responseGuest.data)
              ? responseGuest.data
              : responseGuest.data?.list || [];

            unreadGuest = conversations.reduce((total, item) => {
              const participant = item.participants?.find((participant) => {
                const participantUserId =
                  participant.userID?._id ??
                  participant.userID?.id ??
                  participant.userID ??
                  participant._id;

                return String(participantUserId) === String(guestId);
              });

              return total + Number(participant?.unReadCount || 0);
            }, 0);
          }
        }

        dispatch({
          type: 'SET_UNREAD_COUNTS',
          payload: {
            customer: unreadCustomer,
            guest: unreadGuest,
          },
        });
      } catch (error) {
        console.error('GAGAL MENGAMBIL UNREAD:', error);
      }
    })();

    unreadRequestRef.current = request;

    try {
      await request;
    } finally {
      if (unreadRequestRef.current === request) {
        unreadRequestRef.current = null;
      }
    }
  }, [fetchClient]);

  const refreshUnreadCounts = useCallback(() => {
    if (unreadRefreshTimeoutRef.current) {
      clearTimeout(unreadRefreshTimeoutRef.current);
    }

    unreadRefreshTimeoutRef.current = setTimeout(() => {
      unreadRefreshTimeoutRef.current = null;
      getUnreadCounts();
    }, 300);
  }, [getUnreadCounts]);

  useEffect(() => () => clearTimeout(unreadRefreshTimeoutRef.current), []);

  useEffect(() => {
    if (!customerToken && !guestToken) {
      return;
    }

    getUnreadCounts();
  }, [customerToken, guestToken, getUnreadCounts]);

  useEffect(() => {
    if (!state.lastReadConversationId) {
      return;
    }

    getUnreadCounts();
  }, [state.lastReadConversationId, state.lastReadAt, getUnreadCounts]);

  useConversationSocket(isChatRoute ? null : customerToken, () => {
    refreshUnreadCounts();
  });

  useConversationSocket(isChatRoute ? null : guestToken, () => {
    refreshUnreadCounts();
  });

  return (
    <ChatContext.Provider
      value={{
        state,
        dispatch,
        getUnreadCounts,
        refreshUnreadCounts,

        customerToken,
        guestToken,
        loginIssue,

        loginCustomerChat,
        loginGuestChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
